import { GroupBase, StylesConfig } from 'react-select';

import { OptionType } from 'components/DesignSystem/Select/Select';

export const getCustomStyles = ({
    error,
    isFilterActive,
    isDisabled,
    boldPlaceholder,
}: {
    error: boolean;
    isFilterActive: boolean;
    isDisabled: boolean;
    boldPlaceholder: boolean;
}): StylesConfig<OptionType, boolean, GroupBase<OptionType>> => {
    return {
        control: (base, state) => ({
            ...base,
            color: isFilterActive ? 'var(--text-brand-primary)' : 'var(--text-primary)',
            minHeight: '40px',
            borderRadius: '8px',
            borderColor: error
                ? 'var(--text-danger)'
                : state.isFocused
                  ? 'var(--border-brand)'
                  : isFilterActive
                    ? 'var(--border-quinary)'
                    : 'var(--border-secondary)',
            borderWidth: '1px',
            backgroundColor: isDisabled
                ? 'var(--bg-quaternary)'
                : isFilterActive
                  ? 'var(--bg-tertiary)'
                  : 'var(--bg-primary)',
            boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                borderColor: error
                    ? 'var(--text-danger)'
                    : isFilterActive || state.isFocused
                      ? 'var(--border-brand)'
                      : 'var(--border-primary)',
            },
            minWidth: '16rem',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '2px 12px',
            lineHeight: '1.5',
        }),
        input: (base) => ({
            ...base,
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontFamily: "'Roboto', sans-serif",
        }),
        singleValue: (base) => ({
            ...base,
            color: isFilterActive ? 'var(--text-brand-primary)' : 'var(--text-primary)',
            fontWeight: isFilterActive ? '500' : '400',
            fontSize: '14px',
            fontFamily: "'Roboto', sans-serif",
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: isFilterActive ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
            borderRadius: '4px',
            lineHeight: '1',
            ...(isFilterActive && { border: '1px solid var(--text-brand-primary)' }),
        }),
        multiValueLabel: (base, { data }) => ({
            ...base,
            color: isFilterActive ? 'var(--text-brand-primary)' : 'var(--text-primary)',
            fontSize: '14px',
            fontFamily: "'Roboto', sans-serif",
            backgroundColor: data.value?.isExclude ? '#FFE5E5' : 'transparent',
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: 'var(--fg-tertiary)',
            '&:hover': {
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
            },
        }),
        placeholder: (base) => ({
            ...base,
            color: boldPlaceholder ? 'var(--text-primary)' : 'var(--text-placeholder)',
            fontSize: '14px',
            fontFamily: "'Roboto', sans-serif",
            //fontWeight: boldPlaceholder ? 500 : 400,
            ...(boldPlaceholder && { fontWeight: '500' }),
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '8px',
            border: '1px solid var(--border-secondary)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            minWidth: '15rem',
            width: 'max-content',
        }),
        menuList: (base) => ({
            ...base,
            padding: '4px',
        }),
        option: (base, state) => {
            const optionsList = state.selectProps.options as Array<GroupBase<OptionType> | OptionType>;
            const isGroupedOption =
                Array.isArray(optionsList) &&
                optionsList.some(
                    (entry) =>
                        'options' in (entry as GroupBase<OptionType>) &&
                        Array.isArray((entry as GroupBase<OptionType>).options) &&
                        (entry as GroupBase<OptionType>).options.some(
                            (opt) => opt.value === (state.data as OptionType).value,
                        ),
                );

            return {
                ...base,
                backgroundColor: state.isSelected
                    ? 'var(--bg-brand-primary)'
                    : state.isFocused
                      ? 'var(--bg-secondary)'
                      : 'transparent',
                color: state.isSelected ? 'var(--text-senary)' : 'var(--text-primary)',
                fontSize: '14px',
                fontFamily: "'Roboto', sans-serif",
                padding: `8px 12px 8px ${isGroupedOption ? '20px' : '12px'}`,
                borderRadius: '4px',
                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:active': {
                    backgroundColor: state.isDisabled ? 'transparent' : 'var(--bg-tertiary)',
                },
            };
        },
        indicatorSeparator: (base) => ({
            ...base,
            backgroundColor: 'var(--border-secondary)',
        }),
        dropdownIndicator: (base, state) => ({
            ...base,
            color: isFilterActive
                ? 'var(--text-brand-primary)'
                : state.isFocused
                  ? 'var(--fg-brand-primary)'
                  : 'var(--fg-tertiary)',
            transition: 'all 0.2s ease-in-out',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            '&:hover': {
                color: 'var(--fg-brand-primary)',
            },
        }),
        clearIndicator: (base) => ({
            ...base,
            color: isFilterActive ? 'var(--text-brand-primary)' : 'var(--fg-tertiary)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                color: 'var(--text-danger)',
            },
        }),
        noOptionsMessage: (base) => ({
            ...base,
            color: 'var(--text-tertiary)',
            fontSize: '14px',
            fontFamily: "'Roboto', sans-serif",
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };
};
